import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  useTheme,
  useMediaQuery,
  Fade,
  Divider,
  Avatar,
  Tooltip,
  Collapse,
  Badge,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import LoginIcon from '@mui/icons-material/Login';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useUser } from '../../UserContext';
import { useCart } from '../../contexts/CartContext';

const pages = [
  { title: 'Home', path: '/' },
  { title: 'MAMC', path: '/mamc' },
  // { title: 'Tournaments', path: '/tournaments' },
  // { title: 'Events', path: '/events' },
  { title: 'Players', path: '/players' },
  { title: 'Gallery', path: '/gallery' },
  { title: 'Sponsors', path: '/sponsors' },
  { title: 'Shop', path: '/shop' },
  { title: 'About', path: '/about' },
  { title: 'Auth', path: '/auth', hidden: true },
];

const Navbar = () => {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [profileExpanded, setProfileExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { userData, updateUserData } = useUser();
  const { getCartTotal, toggleCart } = useCart();
  const profileMenuRef = useRef(null);

  const handleOpenNavMenu = event => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenUserMenu = event => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    updateUserData(null);
    navigate('/auth');
    handleCloseUserMenu();
  };

  const _handleLogin = () => {
    navigate('/auth');
  };

  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    return !!token;
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!userData || !userData.email) return '?';
    return userData.email.charAt(0).toUpperCase();
  };

  const toggleProfileExpanded = () => {
    setProfileExpanded(!profileExpanded);
  };

  // Add click outside handler to close the mobile profile dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileExpanded(false);
      }
    };

    // Only add event listener if profile is expanded
    if (profileExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [profileExpanded]);

  // Helper function to close profile dropdown
  const closeProfileDropdown = () => {
    setProfileExpanded(false);
  };

  return (
    <AppBar
      position="sticky"
      elevation={3}
      sx={{
        bgcolor: '#000000',
        borderBottom: '1px solid #FFD700',
        boxShadow: '0 4px 6px rgba(255, 215, 0, 0.1)',
        height: { xs: '70px', md: '80px' },
      }}
    >
      <Container maxWidth="lg" sx={{ height: '100%' }}>
        <Toolbar disableGutters sx={{ minHeight: { xs: '70px', md: '80px' }, py: 0 }}>
          {/* Logo for desktop */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <img
              src="/mirmire_logo_no_background.png"
              alt="Mirmire Logo"
              style={{
                height: '90px',
                width: 'auto',
                filter: 'drop-shadow(0 0 4px rgba(255, 215, 0, 0.5))',
              }}
              loading="lazy"
            />
          </Box>

          {/* Mobile menu and navbar structure for mobile */}
          {isMobile && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                alignItems: 'center',
              }}
            >
              <IconButton
                size="medium"
                aria-label="menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                sx={{
                  color: '#FFFFFF',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'rotate(90deg)',
                  },
                }}
              >
                <MenuIcon fontSize="medium" />
              </IconButton>

              {/* Mobile logo - centered */}
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  position: 'absolute',
                  top: '12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1,
                }}
              >
                <img
                  src="/mirmire_logo_no_background.png"
                  alt="Mirmire Logo"
                  style={{
                    height: '60px',
                    width: 'auto',
                    filter: 'drop-shadow(0 0 4px rgba(255, 215, 0, 0.5))',
                  }}
                  loading="lazy"
                />
              </Box>

              {/* Profile/Login section - moved to right */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* Cart Icon for Mobile */}
                <IconButton
                  onClick={toggleCart}
                  sx={{
                    color: '#FFD700',
                    '&:hover': {
                      bgcolor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  <Badge badgeContent={getCartTotal()} color="error">
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>

                {isAuthenticated() && userData && (
                  <Box ref={profileMenuRef}>
                    <Button
                      onClick={toggleProfileExpanded}
                      endIcon={
                        profileExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
                      }
                      sx={{
                        color: '#FFD700',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '4px',
                        minWidth: 'unset',
                        borderRadius: '50%',
                        '&:hover': {
                          bgcolor: 'rgba(255, 215, 0, 0.1)',
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: '#FFD700',
                          color: '#000000',
                          fontSize: '0.9rem',
                        }}
                      >
                        {getUserInitials()}
                      </Avatar>
                    </Button>

                    <Collapse
                      in={profileExpanded}
                      timeout="auto"
                      unmountOnExit
                      sx={{
                        position: 'absolute',
                        right: 16,
                        width: '200px',
                        zIndex: 1200,
                      }}
                    >
                      <Box
                        sx={{
                          borderRadius: 1,
                          mt: 1,
                          p: 1.5,
                          bgcolor: '#000000',
                          border: '1px solid #FFD700',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.5)',
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#FFD700',
                            fontSize: '0.9rem',
                            fontWeight: 'bold',
                            mb: 1,
                          }}
                        >
                          {userData.email}
                        </Typography>
                        <Typography
                          sx={{
                            color: '#FFFFFF',
                            fontSize: '0.8rem',
                            textTransform: 'capitalize',
                            mb: 1,
                          }}
                        >
                          Role: {userData.role || 'User'}
                        </Typography>

                        <Divider sx={{ borderColor: 'rgba(255, 215, 0, 0.3)', my: 0.5 }} />

                        {(userData?.role === 'volunteer' || userData?.role === 'admin') && (
                          <MenuItem
                            component={RouterLink}
                            to="/volunteer/scores"
                            onClick={() => {
                              handleCloseNavMenu();
                              closeProfileDropdown(); // Close profile dropdown
                            }}
                            sx={{
                              bgcolor: 'transparent',
                              '&:hover': {
                                bgcolor: 'rgba(255, 215, 0, 0.15)',
                              },
                              fontSize: '0.85rem',
                              py: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <SportsSoccerIcon sx={{ fontSize: '0.9rem' }} />
                            <Typography sx={{ color: '#FFFFFF' }}>Match Scores</Typography>
                          </MenuItem>
                        )}

                        {userData?.role !== 'volunteer' && (
                          <MenuItem
                            component={RouterLink}
                            to="/dashboard"
                            onClick={() => {
                              handleCloseNavMenu();
                              closeProfileDropdown(); // Close profile dropdown
                            }}
                            sx={{
                              bgcolor: 'transparent',
                              '&:hover': {
                                bgcolor: 'rgba(255, 215, 0, 0.15)',
                              },
                              fontSize: '0.85rem',
                              py: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <AccountCircleIcon sx={{ fontSize: '0.9rem' }} />
                            <Typography sx={{ color: '#FFFFFF' }}>Dashboard</Typography>
                          </MenuItem>
                        )}

                        {userData?.role === 'admin' && (
                          <MenuItem
                            component={RouterLink}
                            to="/admin/analytics"
                            onClick={() => {
                              handleCloseNavMenu();
                              closeProfileDropdown(); // Close profile dropdown
                            }}
                            sx={{
                              bgcolor: 'transparent',
                              '&:hover': {
                                bgcolor: 'rgba(255, 215, 0, 0.15)',
                              },
                              fontSize: '0.85rem',
                              py: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <BarChartIcon sx={{ fontSize: '0.9rem', color: '#FFD700' }} />
                            <Typography sx={{ color: '#FFFFFF' }}>View Analytics</Typography>
                          </MenuItem>
                        )}

                        {userData?.role === 'admin' && (
                          <MenuItem
                            component={RouterLink}
                            to="/admin"
                            onClick={() => {
                              handleCloseNavMenu();
                              closeProfileDropdown(); // Close profile dropdown
                            }}
                            sx={{
                              bgcolor: 'transparent',
                              '&:hover': {
                                bgcolor: 'rgba(255, 215, 0, 0.15)',
                              },
                              fontSize: '0.85rem',
                              py: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <SettingsIcon sx={{ fontSize: '0.9rem' }} />
                            <Typography sx={{ color: '#FFFFFF' }}>Admin Panel</Typography>
                          </MenuItem>
                        )}

                        <Button
                          onClick={() => {
                            handleCloseNavMenu();
                            closeProfileDropdown(); // Close profile dropdown
                            handleLogout();
                          }}
                          variant="outlined"
                          startIcon={<LogoutIcon sx={{ fontSize: '0.9rem' }} />}
                          sx={{
                            width: '100%',
                            mt: 1,
                            py: 0.5,
                            borderColor: '#FFD700',
                            borderWidth: 1,
                            borderRadius: 1,
                            color: '#FFD700',
                            fontSize: '0.85rem',
                            textTransform: 'none',
                            '&:hover': {
                              bgcolor: 'rgba(255, 215, 0, 0.15)',
                              borderColor: '#FFD700',
                            },
                          }}
                        >
                          Logout
                        </Button>
                      </Box>
                    </Collapse>
                  </Box>
                )}
              </Box>

              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                TransitionComponent={Fade}
                sx={{
                  display: { xs: 'block', md: 'none' },
                  '& .MuiPaper-root': {
                    bgcolor: '#000000',
                    color: '#FFFFFF',
                    width: '100vw',
                    height: '100vh',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    zIndex: 1300,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                    transition: 'all 0.3s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                }}
              >
                {pages.map(page => (
                  <MenuItem
                    key={page.path}
                    component={RouterLink}
                    to={page.path}
                    onClick={handleCloseNavMenu}
                    sx={{
                      ...(page.hidden && {
                        color: '#000000',
                        fontSize: '1px',
                        opacity: 0.1,
                        '&:hover': {
                          opacity: 0.3,
                        },
                      }),
                    }}
                  >
                    <Typography textAlign="center">{page.title}</Typography>
                  </MenuItem>
                ))}
                {!isAuthenticated() && (
                  <MenuItem
                    onClick={() => {
                      handleCloseNavMenu();
                      _handleLogin();
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                    }}
                  >
                    <LoginIcon sx={{ color: '#FFFFFF' }} />
                    <Typography textAlign="center" sx={{ color: '#FFFFFF' }}>
                      Login
                    </Typography>
                  </MenuItem>
                )}
              </Menu>
            </Box>
          )}

          {/* Desktop menu */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {pages.map(page => (
              <Button
                key={page.path}
                component={RouterLink}
                to={page.path}
                onClick={handleCloseNavMenu}
                sx={{
                  my: 2,
                  color: 'white',
                  display: 'block',
                  ...(page.hidden && {
                    color: '#000000',
                    fontSize: '1px',
                    opacity: 0.1,
                    '&:hover': {
                      opacity: 0.3,
                    },
                  }),
                }}
              >
                {page.title}
              </Button>
            ))}
          </Box>

          {/* User dropdown for desktop */}
          {isAuthenticated() ? (
            <Box
              sx={{
                flexShrink: 0,
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1,
              }}
            >
              {/* Cart Icon */}
              <Tooltip title="Shopping Cart">
                <IconButton
                  onClick={toggleCart}
                  sx={{
                    color: '#FFD700',
                    '&:hover': {
                      bgcolor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  <Badge badgeContent={getCartTotal()} color="error">
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Tooltip title="Account settings">
                <Button
                  onClick={handleOpenUserMenu}
                  sx={{
                    bgcolor: 'rgba(255, 215, 0, 0.1)',
                    color: '#FFFFFF',
                    borderRadius: '20px',
                    px: 1,
                    py: 0.5,
                    height: '36px',
                    '&:hover': {
                      bgcolor: 'rgba(255, 215, 0, 0.2)',
                    },
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '1.2rem' }} />}
                >
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: '#FFD700',
                      color: '#000000',
                      fontSize: '0.75rem',
                    }}
                  >
                    {getUserInitials()}
                  </Avatar>
                </Button>
              </Tooltip>
              <Menu
                sx={{
                  mt: '45px',
                  '& .MuiPaper-root': {
                    bgcolor: '#000000',
                    border: '1px solid #FFD700',
                    borderRadius: 2,
                    minWidth: 200,
                  },
                }}
                id="menu-appbar-user"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography sx={{ color: '#FFD700', fontWeight: 'bold', fontSize: '0.85rem' }}>
                    {userData?.email || 'User'}
                  </Typography>
                  <Typography
                    sx={{ color: '#FFFFFF', fontSize: '0.75rem', textTransform: 'capitalize' }}
                  >
                    {userData?.role || 'User'}
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255, 215, 0, 0.3)', my: 0.5 }} />

                {/* Add Volunteer Scores option if user is a volunteer or admin */}
                {(userData?.role === 'volunteer' || userData?.role === 'admin') && (
                  <MenuItem
                    component={RouterLink}
                    to="/volunteer/scores"
                    onClick={handleCloseUserMenu}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(255, 215, 0, 0.1)' },
                      display: 'flex',
                      gap: 1,
                      py: 0.75,
                    }}
                  >
                    <SportsSoccerIcon
                      fontSize="small"
                      sx={{ color: '#FFD700', fontSize: '0.9rem' }}
                    />
                    <Typography sx={{ color: '#FFFFFF', fontSize: '0.85rem' }}>
                      Match Scores
                    </Typography>
                  </MenuItem>
                )}

                {/* Dashboard link for users and admins */}
                {userData?.role !== 'volunteer' && (
                  <MenuItem
                    component={RouterLink}
                    to="/dashboard"
                    onClick={handleCloseUserMenu}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(255, 215, 0, 0.1)' },
                      display: 'flex',
                      gap: 1,
                      py: 0.75,
                    }}
                  >
                    <AccountCircleIcon
                      fontSize="small"
                      sx={{ color: '#FFD700', fontSize: '0.9rem' }}
                    />
                    <Typography sx={{ color: '#FFFFFF', fontSize: '0.85rem' }}>
                      Dashboard
                    </Typography>
                  </MenuItem>
                )}

                {/* Admin panel link for admins */}
                {userData?.role === 'admin' && (
                  <MenuItem
                    component={RouterLink}
                    to="/admin"
                    onClick={handleCloseUserMenu}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(255, 215, 0, 0.1)' },
                      display: 'flex',
                      gap: 1,
                      py: 0.75,
                    }}
                  >
                    <SettingsIcon fontSize="small" sx={{ color: '#FFD700', fontSize: '0.9rem' }} />
                    <Typography sx={{ color: '#FFFFFF', fontSize: '0.85rem' }}>
                      Admin Panel
                    </Typography>
                  </MenuItem>
                )}

                {/* View Analytics link for admins */}
                {userData?.role === 'admin' && (
                  <MenuItem
                    component={RouterLink}
                    to="/admin/analytics"
                    onClick={handleCloseUserMenu}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(255, 215, 0, 0.1)' },
                      display: 'flex',
                      gap: 1,
                      py: 0.75,
                    }}
                  >
                    <BarChartIcon fontSize="small" sx={{ color: '#FFD700', fontSize: '0.9rem' }} />
                    <Typography sx={{ color: '#FFFFFF', fontSize: '0.85rem' }}>
                      View Analytics
                    </Typography>
                  </MenuItem>
                )}

                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    '&:hover': { bgcolor: 'rgba(255, 215, 0, 0.1)' },
                    display: 'flex',
                    gap: 1,
                    py: 0.75,
                  }}
                >
                  <LogoutIcon fontSize="small" sx={{ color: '#FFD700', fontSize: '0.9rem' }} />
                  <Typography sx={{ color: '#FFFFFF', fontSize: '0.85rem' }}>Logout</Typography>
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Box
              sx={{
                flexShrink: 0,
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1,
              }}
            >
              {/* Cart Icon for non-authenticated users */}
              <Tooltip title="Shopping Cart">
                <IconButton
                  onClick={toggleCart}
                  sx={{
                    color: '#FFD700',
                    '&:hover': {
                      bgcolor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  <Badge badgeContent={getCartTotal()} color="error">
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Button
                onClick={_handleLogin}
                startIcon={<LoginIcon />}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: '#FFFFFF',
                  borderRadius: '20px',
                  px: 2,
                  py: 0.5,
                  height: '36px',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    borderColor: '#FFFFFF',
                  },
                }}
              >
                Login
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
